import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "../components/site/MarketingLayout";
import QrifyMark from "../components/site/QrifyMark";

const sections = [
  "Who we are",
  "Personal data we collect and why",
  "Lawful basis",
  "Who we share data with",
  "Cross-border processing",
  "How long we keep data",
  "Your rights",
  "Children",
  "Security",
  "Changes",
  "Contact",
];

const collectionRows = [
  [
    "Create and secure your account",
    "Mobile number, OTP, device session tokens",
    "Sign-up / login",
  ],
  [
    "Profile & emergency QR identity",
    "Name, email, DOB, gender, address, photo; Aadhaar only if you choose to provide it",
    "Profile setup",
  ],
  [
    "Medical context for emergencies",
    "Blood group, conditions, allergies, medications, other health details you enter",
    "Medical screen",
  ],
  [
    "Notify helpers",
    "Emergency contact names and phones; family member details",
    "Emergency / family features",
  ],
  [
    "Locate and assist in emergencies",
    "Approximate location when you trigger emergency / test call or share location",
    "Emergency features",
  ],
  [
    "AI assistance (Swastika / injury analysis)",
    "Text, voice, images/video you submit; relevant profile/medical context",
    "When you use AI features",
  ],
  [
    "Push notifications",
    "Device push IDs (FCM / OneSignal)",
    "After you allow notifications",
  ],
  [
    "Payments & plans",
    "Payment order/payment references via Razorpay",
    "When you purchase/renew",
  ],
  [
    "Support & website contact",
    "Name, email, phone, message",
    "When you contact us",
  ],
  [
    "Security & abuse prevention",
    "Technical logs (with personal data minimised/redacted)",
    "Continuously",
  ],
];

const retentionRows = [
  ["OTP", "Minutes"],
  [
    "Account & medical profile",
    "Until you delete your account or we no longer need it for the stated purpose",
  ],
  ["Emergency scan / location logs", "About 24 months, then review"],
  [
    "AI chats / uploaded media",
    "About 12 months, or earlier on erasure request",
  ],
  ["Payment records", "As required for tax and finance laws"],
  ["Security logs", "About 12 months"],
];

const PrivacyPolicy = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <MarketingLayout>
      <div className="sv-policy">
        <section className="sv-policy__hero">
          <div className="sv-container">
            <h1 className="sv-h1">
              Privacy Policy
            </h1>
            <p className="sv-policy__hero-meta">
              Svasth / <QrifyMark /> · Data Fiduciary: Alegion Health Tech Private
              Limited · Last updated: 10 August 2026
            </p>
            <p className="sv-policy__hero-meta">
              Also review our{" "}
              <Link to="/terms">Terms & Conditions</Link>
            </p>
          </div>
        </section>

        <div className="sv-policy__content">
          <div className="sv-toc">
            <strong>Contents</strong>
            <ol>
              {sections.map((title, i) => (
                <li key={title}>
                  <a
                    href={`#section-${i + 1}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo(`section-${i + 1}`);
                    }}
                  >
                    {title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          <p>
            This Policy is provided under the Digital Personal Data Protection
            Act, 2023. It applies to the Svasth / <QrifyMark /> app,{" "}
            <QrifyMark /> Emergency Lite, and related services at svasth.ai.
            Please read it before using our services.
          </p>

          <section id="section-1">
            <h2>1. Who we are</h2>
            <p>
              ALEgION Health Tech Private Limited operates Svasth / <QrifyMark />{" "}
              emergency identification and response services.
            </p>
            <p>
              <strong>Grievance Officer (India)</strong>
            </p>
            <ul>
              <li>
                Email: tech@svasth.ai 
                
              </li>
              <li>
                Postal: ALEgION Health Tech Private Limited, New No. 7C, Perumal
                Koil Street, Nerkundram, Chennai, Tamil Nadu 600107
              </li>
            </ul>
          </section>

          <section id="section-2">
            <h2>2. Personal data we collect and why</h2>
            <div className="sv-policy__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Purpose</th>
                    <th>Data we use</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {collectionRows.map((row) => (
                    <tr key={row[0]}>
                      <td>{row[0]}</td>
                      <td>{row[1]}</td>
                      <td>{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section id="section-3">
            <h2>3. Lawful basis</h2>
            <p>
              We process your data based on your consent (after this Policy)
              and, where applicable, legitimate uses under the DPDP Act (for
              example, using scan and location data to deliver emergency
              assistance you or a rescuer initiate via QR).
            </p>
            <p>
              You may withdraw consent at any time (Account → Privacy controls,
              or email the Grievance Officer). Withdrawal does not affect
              processing already completed lawfully. Some emergency or legal
              retention may continue as required by law.
            </p>
          </section>

          <section id="section-4">
            <h2>4. Who we share data with (processors / partners)</h2>
            <p>
              We use specialised providers under arrangements that require
              appropriate security:
            </p>
            <ul>
              <li>ACL / SMS &amp; voice gateways — OTP and emergency calls/SMS</li>
              <li>Firebase Cloud Messaging &amp; OneSignal — push notifications</li>
              <li>
                Amazon Web Services (S3, typically Mumbai region) — media storage
              </li>
              <li>
                Google Gemini — AI analysis (may involve processing outside
                India)
              </li>
              <li>Razorpay — payments</li>
              <li>
               Insurance providers / Religare and hospital/ambulance partners — where you are
                enrolled or an emergency dispatch is needed
              </li>
              <li>MongoDB Atlas — database hosting</li>
            </ul>
            <p>We do not sell your personal data.</p>
          </section>

          <section id="section-5">
            <h2>5. Cross-border processing</h2>
            <p>
              Some processors (notably generative AI and global push platforms)
              may process data outside India. Where this occurs, we disclose it
              here and rely on contractual and organisational safeguards.
            </p>
          </section>

          <section id="section-6">
            <h2>6. How long we keep data</h2>
            <div className="sv-policy__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Typical retention</th>
                  </tr>
                </thead>
                <tbody>
                  {retentionRows.map((row) => (
                    <tr key={row[0]}>
                      <td>{row[0]}</td>
                      <td>{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section id="section-7">
            <h2>7. Your rights</h2>
            <p>You may request:</p>
            <ul>
              <li>Access to and correction of your personal data</li>
              <li>
                Erasure of your account and related personal data (subject to
                legal retention)
              </li>
              <li>Withdrawal of consent for optional processing</li>
              <li>Grievance redressal via the Grievance Officer</li>
            </ul>
            <p>
              We will respond within a reasonable period. If unsatisfied, you
              may complain to the Data Protection Board of India.
            </p>
          </section>

          <section id="section-8">
            <h2>8. Children</h2>
            <p>
              Our services are directed to adults and guardians managing family
              profiles. We do not knowingly process children&apos;s data for
              non-essential purposes without verifiable parental/guardian
              consent as required by law.
            </p>
          </section>

          <section id="section-9">
            <h2>9. Security</h2>
            <p>
              We implement reasonable security safeguards, including access
              controls, encryption in transit, secure cloud storage, and
              restrictions on logging sensitive data. No method of transmission
              is 100% secure; please protect your device and OTP.
            </p>
          </section>

          <section id="section-10">
            <h2>10. Changes</h2>
            <p>
              We may update this Policy. Material changes will be reflected by
              updating the &quot;Last updated&quot; date and, where appropriate,
              in-app notice or renewed consent.
            </p>
          </section>

          <section id="section-11">
            <h2>11. Contact</h2>
            <p>
              Questions or rights requests: tech@svasth.ai
              
            </p>
            <p>
              Website:{" "}
              <a href="https://svasth.ai" rel="noopener noreferrer">
                https://svasth.ai
              </a>
            </p>
            <p>
              Terms & Conditions:{" "}
              <Link to="/terms">https://svasth.ai/terms</Link>
            </p>
          </section>

          <p className="sv-policy__foot">
            © {new Date().getFullYear()} ALEgION Health Tech Private Limited ·
            Svasth / <QrifyMark />
          </p>
        </div>
      </div>
    </MarketingLayout>
  );
};

export default PrivacyPolicy;
